import { useContext } from 'react'
import { useSelector } from 'react-redux'
import { Helmet } from 'react-helmet-async'
import PropTypes from 'prop-types'

import { faGlobe } from '@fortawesome/pro-light-svg-icons'

import { sortArrayBy } from '@domain-group/javascript-utils'

import {
  Box, Button, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import ListBlankState from '@components/ListBlankState'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import { generateUrls } from '@hooks/useServiceProduct'
import useServiceProducts from '@hooks/useServiceProducts'

import ServiceProductListItem from '../ServiceProductListItem'

const buildPageFilterFields = (entities) => {
  const { services } = entities

  const style = { marginBottom: 16 }
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title or ID',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: 'All',
        inputComponent: 'select',
        style,
      },
      key: 'service_id',
      label: 'Services',
      values: sortArrayBy(Object.values(services), 'asc', 'title').map(service => ({
        key: service.id,
        label: service.title,
      })),
    },
    {
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: 'All',
        inputComponent: 'select',
        style,
      },
      key: 'locked_status',
      label: 'Locked Status',
      values: [
        { key: 'true', label: 'Yes' },
        { key: 'false', label: 'No' },
      ],
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'providers',
      },
      key: 'assigned_provider_id',
    },
  ]
}

const PageContent = (props) => {
  const { pageFilters } = props

  const entities = useSelector(reduxState => reduxState.entities)

  const serviceProductsPayload = useServiceProducts({
    filters: pageFilters,
    performHttpRequests: true,
    requestOptions: { include_service_product_provider_ids: true },
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredServiceProducts,
    filteredServiceProductsCount,
    hasFilteredServiceProducts,
    loading,
  } = serviceProductsPayload

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters,
      showCreateServiceProductModal,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const { serviceProductsIndexUrl } = generateUrls()

  return (
    <>
      <Helmet>
        <title>Products | Realhub</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button buttonStyle="primaryCreate" onClick={() => showCreateServiceProductModal()} size="medium">
            Create
          </Button>
        )}
        activeTabBarItemKey="serviceProducts"
        tabBarItems={[
          {
            href: serviceProductsIndexUrl,
            icon: faGlobe,
            key: 'serviceProducts',
            title: 'Products',
          },
        ]}
        title="Products"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredServiceProducts && <ListBlankState />}

            {!loading && hasFilteredServiceProducts && (
              <DashboardModule title="Products">
                <Box flexDirection="column">
                  <Box flexDirection="column">
                    {filteredServiceProducts.map(serviceProduct => (
                      <ServiceProductListItem key={serviceProduct.id} serviceProduct={serviceProduct} />
                    ))}
                  </Box>

                  <DashboardModule.LoadMoreFooter
                    callbacks={{ loadMore }}
                    canLoadMore={canLoadMore}
                    entityCount={filteredServiceProductsCount}
                    loading={loading}
                  />
                </Box>
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              filterFields={buildPageFilterFields(entities)}
              pageFilters={pageFilters}
              title="Filters"
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

PageContent.propTypes = {
  pageFilters: PropTypes.object.isRequired,
}

export default PageContent
